/**
 * Utilidades de conversión y mapeo para el módulo de Margen Bruto Zonal (MBZ).
 *
 * Este archivo centraliza las funciones de transformación de datos entre:
 * - Formularios del frontend (ZonalGrossMarginCreationForm)
 * - API del backend (CreateZonalGrossMarginBody, ZonalGrossMarginDetail)
 *
 * Incluye cálculos automáticos como la inversión total y conversión de tipos
 * (arrays de strings para selects múltiples vs números para la API).
 */

import { formatCurrencyInputToNumber } from "@/utils/query-string";
import {
  CreateZonalGrossMarginBody,
  ZonalGrossMarginDetail,
} from "@/services/zonal-gross-margin.service";
import { ZonalGrossMarginCreationForm } from "../create/schema/zonal-gross-margin-creation.schema";

/**
 * Convierte un número a string con coma como separador decimal (formato es-AR).
 * Utilizado para campos que usan NumericFieldWithComma.
 *
 * @param value - Valor numérico a convertir
 * @returns String con coma como separador decimal
 */
const formatNumberWithComma = (value: number | undefined): string => {
  if (value === undefined || value === null) return "";
  return String(value).replace(".", ",");
};

/**
 * Valores por defecto para el formulario de creación/edición de MBZ.
 * Todos los campos se inicializan vacíos o como arrays vacíos para selects múltiples.
 */
export const getDefaultZonalGrossMarginFormValues = (): ZonalGrossMarginCreationForm => ({
  idZonaProductiva: [],
  idGranoProductivo: [],
  idCampanaProductiva: [],
  cotizacionDolar: "",
  precioCereal: "",
  rindeEstimadoZonal: "",
  precioAgroquimicos: "",
  precioFertilizantes: "",
  precioSemillas: "",
  precioLabores: "",
  precioSeguros: "",
  costoImplantacion: "",
  precioAlquiler: "",
  gastoDeCosecha: "",
  inversionTotal: "",
  equilibrio: "",
});

/**
 * Extrae el primer elemento de un valor que puede ser array o no.
 * Útil para convertir selects múltiples (arrays) a valores simples para la API.
 *
 * @param value - Valor que puede ser array o valor simple
 * @returns El primer elemento convertido a número
 */
export const getSelectedNumber = (value: unknown) => {
  const selected = Array.isArray(value) ? value[0] : value;
  return Number(selected);
};

/**
 * Construye el body de la request para crear o actualizar un MBZ.
 * Convierte los valores del formulario al formato esperado por la API:
 * - Selects múltiples (arrays de strings) → números simples
 * - Campos de moneda (strings con formato) → números
 * - Incluye el ID solo si se está actualizando (endpoint PUT)
 *
 * @param values - Valores del formulario
 * @param id - ID opcional para actualizaciones (endpoint PUT)
 * @returns Body formateado para la API
 */
export const buildZonalGrossMarginRequestBody = (
  values: ZonalGrossMarginCreationForm,
  id?: number,
): CreateZonalGrossMarginBody => ({
  idGranoProductivo: getSelectedNumber(values.idGranoProductivo),
  idZonaProductiva: getSelectedNumber(values.idZonaProductiva),
  idCampanaProductiva: getSelectedNumber(values.idCampanaProductiva),
  cotizacionDolar: formatCurrencyInputToNumber(values.cotizacionDolar),
  precioCereal: formatCurrencyInputToNumber(values.precioCereal),
  rindeEstimadoZonal: formatCurrencyInputToNumber(values.rindeEstimadoZonal),
  precioAgroquimicos: formatCurrencyInputToNumber(values.precioAgroquimicos),
  precioFertilizantes: formatCurrencyInputToNumber(values.precioFertilizantes),
  precioSemillas: formatCurrencyInputToNumber(values.precioSemillas),
  precioLabores: formatCurrencyInputToNumber(values.precioLabores),
  precioSeguros: formatCurrencyInputToNumber(values.precioSeguros),
  costoImplantacion: formatCurrencyInputToNumber(values.costoImplantacion),
  ...(values.precioAlquiler?.trim()
    ? { precioAlquiler: formatCurrencyInputToNumber(values.precioAlquiler) }
    : {}),
  gastoDeCosecha: formatCurrencyInputToNumber(values.gastoDeCosecha),
  inversionTotal: formatCurrencyInputToNumber(values.inversionTotal),
  equilibrio: formatCurrencyInputToNumber(values.equilibrio),
  ...(id !== undefined && { id }),
});

/**
 * Calcula automáticamente el costo de implantación.
 * Fórmula: Agroquímicos + Fertilizantes + Semillas + Labores + Seguros
 *
 * Este cálculo se actualiza en tiempo real cuando el usuario modifica cualquiera
 * de los campos componentes en el formulario.
 *
 * @param values - Valores parciales del formulario
 * @returns Suma de los componentes de inversión en USD/ha
 */
export const calculateImplantationCost = (
  values?: Partial<ZonalGrossMarginCreationForm>,
) => {
  return (
    formatCurrencyInputToNumber(values?.precioAgroquimicos ?? 0) +
    formatCurrencyInputToNumber(values?.precioFertilizantes ?? 0) +
    formatCurrencyInputToNumber(values?.precioSemillas ?? 0) +
    formatCurrencyInputToNumber(values?.precioLabores ?? 0) +
    formatCurrencyInputToNumber(values?.precioSeguros ?? 0)
  );
};

/**
 * Calcula automáticamente la inversión total.
 * Fórmula: Costo de implantación + Alquiler
 *
 * Este cálculo se actualiza en tiempo real cuando el usuario modifica cualquiera
 * de los campos componentes en el formulario.
 *
 * @param values - Valores parciales del formulario
 * @returns Suma de costo de implantación y alquiler en USD/ha
 */
export const calculateInvestmentTotal = (
  values?: Partial<ZonalGrossMarginCreationForm>,
) => {
  return (
    calculateImplantationCost(values) +
    formatCurrencyInputToNumber(values?.precioAlquiler ?? 0)
  );
};

/**
 * Convierte un detalle de MBZ desde la API al formato del formulario.
 * Utilizado al cargar un MBZ existente en la página de detalle.
 *
 * Transformaciones:
 * - IDs numéricos → arrays de strings para selects múltiples
 * - Números → strings para campos de texto/moneda
 * - Campos con formato es-AR (rinde, gasto de cosecha, equilibrio) usan coma como decimal
 *
 * @param detail - Detalle de MBZ desde la API
 * @returns Valores formateados para el formulario
 */
export const mapZonalGrossMarginDetailToFormValues = (
  detail: ZonalGrossMarginDetail,
): ZonalGrossMarginCreationForm => ({
  idZonaProductiva: detail.idZonaProductiva ? [String(detail.idZonaProductiva)] : [],
  idGranoProductivo: detail.idGranoProductivo ? [String(detail.idGranoProductivo)] : [],
  idCampanaProductiva: detail.idCampProd ? [String(detail.idCampProd)] : [],
  cotizacionDolar: detail.cotizacionDolar !== undefined ? String(detail.cotizacionDolar) : "",
  precioCereal: detail.precioCereal !== undefined ? String(detail.precioCereal) : "",
  rindeEstimadoZonal:
    detail.rindeEstimadoZonal !== undefined ? formatNumberWithComma(detail.rindeEstimadoZonal) : "",
  precioAgroquimicos:
    detail.precioAgroquimicos !== undefined ? String(detail.precioAgroquimicos) : "",
  precioFertilizantes:
    detail.precioFertilizantes !== undefined ? String(detail.precioFertilizantes) : "",
  precioSemillas: detail.precioSemillas !== undefined ? String(detail.precioSemillas) : "",
  precioLabores: detail.precioLabores !== undefined ? String(detail.precioLabores) : "",
  precioSeguros: detail.precioSeguros !== undefined ? String(detail.precioSeguros) : "",
  costoImplantacion:
    detail.costoImplantacion !== undefined ? String(detail.costoImplantacion) : "",
  precioAlquiler:
    detail.precioAlquiler !== undefined ? String(detail.precioAlquiler) : "",
  gastoDeCosecha:
    detail.gastoDeCosecha !== undefined ? formatNumberWithComma(detail.gastoDeCosecha) : "",
  inversionTotal: detail.inversionTotal !== undefined ? String(detail.inversionTotal) : "",
  equilibrio:
    detail.equilibrio !== undefined ? formatNumberWithComma(detail.equilibrio) : "",
});
